import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { authActions } from "../redux/slice/auth.slice";

const BugReport = () => {
  const currentUser = useSelector(authActions.selectors.getCurrentUser);
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [descriptionError, setDescriptionError] = useState("");

  const handleFilesChange = (event) => {
    const files = Array.from(event.target.files || []).slice(0, 5);
    setAttachments(files);
    event.target.value = null;
  };

  const attachmentList = useMemo(
    () => attachments.map((file) => ({ name: file.name, size: file.size })),
    [attachments]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!description.trim()) {
      const message = "A short description helps us reproduce the issue.";
      setDescriptionError(message);
      toast.error(message);
      return;
    }
    setDescriptionError("");

    setIsSubmitting(true);
    setStatus("");
    setError("");

    const formData = new FormData();
    formData.append("description", description.trim());
    formData.append("email", email);
    attachments.forEach((file) => formData.append("attachments", file));

    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${process.env.REACT_APP_AUTH_URL}/bug-report`, {
        method: "POST",
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const failure = await response.text();
        throw new Error(failure || "Unable to submit the bug report. Please try again.");
      }

      setStatus("success");
      toast.success("Thanks for the report – we'll get back to you shortly.");
      setDescription("");
      setAttachments([]);
    } catch (submissionError) {
      setStatus("error");
      const message = submissionError?.message || "Something went wrong while uploading the report.";
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 4 }, maxWidth: 900, margin: "0 auto" }}>
      <Stack spacing={2} mb={4}>
        <Typography variant="h4">Bug Report</Typography>
        <Typography variant="body1" color="text.secondary">
          Fill in the details below so our team can investigate quickly. Include steps to reproduce, expected behavior, and any visuals that capture what went wrong.
        </Typography>
      </Stack>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: { xs: 3, md: 4 } }} elevation={3}>
        <Stack spacing={3}>
          <TextField
            label="Your Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            helperText="Pre-filled from your account but editable if you want to use a different address."
          />
          <TextField
            label="Describe the issue"
            placeholder="What happened? What did you expect to happen instead?"
            minRows={6}
            multiline
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              if (event.target.value.trim()) {
                setDescriptionError("");
              }
            }}
            fullWidth
            required
            error={Boolean(descriptionError)}
            helperText={descriptionError || ""}
          />

          <Box>
            <input
              accept="image/*,video/*,audio/*"
              style={{ display: "none" }}
              id="bug-report-attachments"
              type="file"
              multiple
              onChange={handleFilesChange}
            />
            <label htmlFor="bug-report-attachments">
              <Button variant="outlined" component="span" startIcon={<AttachFileIcon />}>
                Upload screenshots
              </Button>
            </label>
            <Typography variant="caption" display="block" mt={1} color="text.secondary">
              Attach up to 5 files (images, video, or audio) that demonstrate the issue.
            </Typography>
          </Box>

          {attachmentList.length ? (
            <Box>
              <Typography variant="subtitle2" mb={1}>
                Attached files
              </Typography>
              <List disablePadding>
                {attachmentList.map((file, index) => (
                  <ListItem key={`${file.name}-${index}`} disableGutters>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null}

          <Button
            variant="contained"
            type="submit"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? "Submitting…" : "Submit bug report"}
          </Button>

          {status === "success" && (
            <Typography variant="body1" color="success.main">
              Thank you! We received your report and will review it within one business day.
            </Typography>
          )}
          {status === "error" && (
            <Typography variant="body2" color="error.main">
              {error || "Unable to submit the report. Please try again."}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default BugReport;
